import { ChevronLeft, ChevronRight, InfoIcon, XCircle } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { useUpdatePickupOrder } from '@/api/pickup-orders/queries/usePickupOrders'
import { PickupOrder, PickupOrderStatus } from '@/api/pickup-orders/types/PickupOrder'
import { PkgWarehouse } from '@/api/user-package/types/UserPkg'
import { AppTooltip } from '@/components/AppTooltip'
import { BrandPreview } from '@/components/BrandPreview'
import { Divider } from '@/components/Divider'
import { Button } from '@/components/ui/button'
import { formatDateEu } from '@/helpers/formatDate'
import { getPickupOrderStatus } from '@/helpers/pkgUtils'
import { cn } from '@/lib/utils'

// const MS_12H = 12 * 60 * 60 * 1000

interface Props {
  order: PickupOrder
}

const formatDate = (dateString: string): string => formatDateEu(dateString)

// const isPickupWithin24Hours = (pickupDate: string): boolean => {
//   const pickupDateTime = new Date(pickupDate.split('T')[0])
//   const now = new Date()
//   now.setHours(0, 0, 0, 0)
//   pickupDateTime.setHours(0, 0, 0, 0)
//   return pickupDateTime <= now && pickupDateTime >= new Date(now.getTime() - MS_12H)
// }

interface PickupOrderActionsMenuProps {
  order: PickupOrder
}

const PickupOrderActionsMenu = ({ order: _order }: PickupOrderActionsMenuProps) => {
  const { t } = useTranslation()
  const updateMutation = useUpdatePickupOrder()
  // const { showSuccessToast, showErrorToast } = useToast()
  // const { confirm, ConfirmDialog } = useConfirm()
  const isCancelable = false //order.status === PickupOrderStatus.Pending || order.status === PickupOrderStatus.FalseBarcode
  const within24h = false //isPickupWithin24Hours(order.pickupDate)
  const canCancel = isCancelable && !within24h
  // const handleCancelPickupClick = async () => {
  //   if (!canCancel) return
  //   const confirmed = await confirm({
  //     title: t('pickup.cancelPickup'),
  //     description: t('pickup.cancelPickupConfirm'),
  //     helperText: <Trans i18nKey='pickup.cancelPickupHelperText' components={{ bold: <span className='font-bold' /> }} />,
  //     confirmLabel: t('pickup.cancelPickup'),
  //     cancelLabel: t('common.back'),
  //     variant: 'destructive',
  //   })
  //   if (!confirmed) return
  //   try {
  //     await updateMutation.mutateAsync({ id: order.id, status: PickupOrderStatus.Cancelled })
  //     showSuccessToast(t('pickup.pickupCancelled'), t('pickup.pickupCancelledSuccess'))
  //   } catch {
  //     showErrorToast(t('pickup.cancelFailed'), t('pickup.cancelFailedHelp'))
  //   }
  // }

  const cancelButton = (
    <Button
      icon={<XCircle className={cn('size-4', canCancel ? 'text-icon-primary' : 'text-icon-disabled')} />}
      variant='secondary'
      // onClick={canCancel ? handleCancelPickupClick : undefined}
      disabled={!canCancel || updateMutation.isPending}
      className={cn(
        'gap-2 text-xs disabled:pointer-events-auto disabled:cursor-default disabled:hover:shadow-none',
        !canCancel && 'text-text-disabled cursor-default bg-[#F7F8F9] hover:shadow-none'
      )}
    >
      {t('pickup.cancelPickup')}
    </Button>
  )
  return (
    <AppTooltip triggerProps={{ asChild: true }} content={t('pickup.cancelDisabled')} asChild side='top'>
      {cancelButton}
    </AppTooltip>
  )
  // return (
  //   <>
  //     {within24h ? (
  //       <AppTooltip triggerProps={{ asChild: true }} content={t('pickup.cancelWithin24h')} asChild side='top'>
  //         {cancelButton}
  //       </AppTooltip>
  //     ) : (
  //       cancelButton
  //     )}
  //     {ConfirmDialog}
  //   </>
  // )
}

export const PickupOrderPreview = ({ order }: Props) => {
  const { t } = useTranslation()
  const [_, setSearchParams] = useSearchParams()
  const [isReqMenuOpen, setIsReqMenuOpen] = useState(false)
  const status = getPickupOrderStatus(order, t)
  const warehouse = order.warehouse as PkgWarehouse
  const isFalseBarcode = order.status === PickupOrderStatus.FalseBarcode
  const isPendingBardcode = !order.barcodeImg
  const allowUploadBarcode = isFalseBarcode || isPendingBardcode
  const isCancelled = order.status === PickupOrderStatus.Cancelled
  const showMoreActions = !isCancelled

  const handleUploadBarcode = () => {
    setSearchParams(prev => {
      prev.set('pickupUploadOrderId', order.id)
      return prev
    })
  }

  return (
    <>
      <div className='border-border-default flex flex-col gap-4 overflow-hidden rounded-2xl border bg-white p-4 shadow-sm'>
        {/* Header Section */}
        <div className='flex items-start justify-between'>
          {/* Brand and Warehouse */}
          <BrandPreview brand={'Primark'} warehouse={warehouse} />

          {/* Status Badge */}
          {status && (
            <div className='text-text-secondary bg-button-primary-selected-segment rounded-full px-3 py-1 text-center text-xs'>
              {status}
            </div>
          )}
        </div>

        {/* Order Details */}
        <div className='flex flex-wrap items-center gap-8 text-sm'>
          {/* Creation Date */}
          <div>
            <p className='text-text-secondary mb-1 text-xs'>{t('pickup.primarkOrderDate')}</p>
            <p className='font-medium'>{formatDate(order.createdAt)}</p>
          </div>

          {/* Order Number */}
          <div>
            <p className='text-text-secondary mb-1 text-xs'>{t('pickup.orderNumber')}</p>
            <p className='font-medium'>{order.orderNumber}</p>
          </div>

          {/* Pickup Date */}
          <div className='relative'>
            <AppTooltip content={t('pickup.pickupDateTooltip')}>
              <p className='text-text-secondary mb-1 flex items-center gap-1 text-xs'>
                {t('pickup.pickupFromBranch')}
                <InfoIcon className='size-2.5' />
              </p>
            </AppTooltip>
            <p className='font-medium'>{formatDate(order.pickupDate)}</p>
          </div>
        </div>
        {(allowUploadBarcode || showMoreActions) && <Divider />}

        {/* Bottom row: more actions + upload barcode */}
        <div className='flex items-center justify-end gap-2'>
          {showMoreActions && (
            <>
              <div
                className={cn(
                  'transform transition-all duration-300 ease-in-out',
                  isReqMenuOpen ? 'translate-x-0 opacity-100' : 'pointer-events-none -translate-x-2 opacity-0'
                )}
              >
                <PickupOrderActionsMenu order={order} />
              </div>
              <Button variant='ghost' size='sm' className='gap-1' onClick={() => setIsReqMenuOpen(p => !p)}>
                {isReqMenuOpen ? (
                  <ChevronLeft className='text-text-secondary size-4' />
                ) : (
                  <ChevronRight className='text-text-secondary size-4' />
                )}
                <span className='text-text-secondary text-xs'>{t('pkg.moreActions')}</span>
              </Button>
            </>
          )}
          {allowUploadBarcode && (
            <Button type='button' onClick={handleUploadBarcode}>
              {isFalseBarcode ? t('pickup.uploadNewBarcode') : t('pickup.uploadBarcode')}
            </Button>
          )}
        </div>
      </div>
    </>
  )
}
